import type { CSSProperties, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconCheck } from '#src/atoms/icons/system/iconCheck.ts';
import { iconClock } from '#src/atoms/icons/system/iconClock.ts';
import { iconGift } from '#src/atoms/icons/system/iconGift.ts';
import { iconX } from '#src/atoms/icons/system/iconX.ts';
import { glossary } from '#src/lib/glossary.ts';

import { Chip } from '../index.tsx';

export type WidgetChipState =
  | 'default'
  | 'new'
  | 'timeLeft'
  | 'reward'
  | 'done'
  | 'fail'
  | 'progress';

const COLOR_BY_STATE = {
  default: 'transparent',
  new: 'glow',
  timeLeft: 'transparent',
  reward: 'purple',
  done: 'purple',
  fail: 'red',
  progress: 'transparent',
} as const;

const ICON_BY_STATE = {
  timeLeft: iconClock,
  reward: iconGift,
  done: iconCheck,
  fail: iconX,
} as const;

const Root = styled(Chip)`
  position: relative;
  overflow: clip;
`;

const Content = styled.span`
  &::before {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: var(--progress);
    background: var(--c-gradient-hotstreak);
    z-index: -1;
    border-radius: inherit;
    content: '';
  }
`;

const clamp = (value: number) => Math.min(1, Math.max(0, value));

type Props = {
  state?: WidgetChipState;
  progress?: number;
  children?: ReactNode;
  className?: string;
};

export const WidgetChip = ({
  state = 'default',
  progress = 0,
  children,
  className,
}: Props) => (
  <Root color={COLOR_BY_STATE[state]} size="small" className={className}>
    {state === 'progress' ? (
      <>
        <Content
          style={{ '--progress': `${clamp(progress) * 100}%` } as CSSProperties}
        >
          {children}
        </Content>
      </>
    ) : (
      <>
        {state === 'new' && <FormattedMessage {...glossary.new} />}
        {state in ICON_BY_STATE && (
          <FontAwesomeIcon
            icon={ICON_BY_STATE[state as keyof typeof ICON_BY_STATE]}
          />
        )}
        {children}
      </>
    )}
  </Root>
);
